import React, { useEffect, useState } from 'react';
import { Button, Typography, Stack, Divider, Box, CircularProgress, Snackbar, Alert, Tooltip } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import DownloadIcon from '../../icons/DownloadIcon.svg';
import { TableContainer, Table, TableBody, TableCell, TableHead, TableRow, TablePagination } from '@mui/material';
import Filter from '../components/Filter';
import { selectOwnerDocuments, selectOwnerDocumentsDataPages, selectOwnerDocumentsState } from './sagas/documentsSlice';
import { OwnerDocumentDto } from '@inosio/payments-sdk';
import { fetchOwnerDocumentURL, getDownloading, selectOwnersState } from '../../pmui/owners/sagas/ownersSlice';
import { getFriendlyWordsFromDocumentType } from '../../pmui/owners/utils';
import ComingSoonImg from '../../icons/ComingSoon.svg';
import { selectPropertyGroupDetailsState } from '../../shared/sagas/propertyGroups/propertyGroupsSlice';
import SpinnerWithNoConfirmation from '../../shared/Modal/spinnerWithNoConfirmation';
import Error500 from '../../icons/505Error.svg';
import ReviewOtpIcon from '../../icons/ReviewOtpIcon.svg';
import { MIME_TYPES_VIEWABLE_IN_BROWSER } from '../../utils/constants';
import { getMimeTypeFromFileName } from '../../shared/util/extractors';
import BrowserNotSupportedIcon from '@mui/icons-material/BrowserNotSupported';
import { convertSqlDateToUSCalendarDate } from '../../shared/util/converters';
import { selectCurrentUser } from '../../shared/sagas/auth/authSlice';

export default function Documents() {
  const dispatch = useDispatch();

  //selectors
  const ownerDocumentsDataBook = useSelector(selectOwnerDocumentsDataPages);
  const ownerDocuments = useSelector(selectOwnerDocuments);
  const {
    isDownloading,
    isDownloadingFailed,
    isFetchingOwnerDetailsFailed,
    isFetchingOwnerDetailsSuccess,
    isFetchingOwnerDocumentURL,
    isFetchingOwnerDocumentURLSuccess,
    ownerDocumentURL,
    isFetchingOwnerDocumentURLFailed,
  } = useSelector(selectOwnersState);
  const { companyInfo, isGettingCompanySuccess, isGettingCompanyFailed } = useSelector(selectPropertyGroupDetailsState);
  const currentUser = useSelector(selectCurrentUser);
  const { isFetchingOwnerDocumentsFailed } = useSelector(selectOwnerDocumentsState);

  //local
  const [selectedDocumentUuid, setSelectedDocumentUuid] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [recordStartIndex, setRecordStartIndex] = useState(0);
  const [rangeSize, setRangeSize] = useState(100);
  const [initialLoadErrorMessage, setInitialLoadErrorMessage] = useState<string | null>(null);
  const [docDownloadErrorMessage, setDocDownloadErrorMessage] = useState<string | null>(null);
  const [showLocalSpinner, setShowLocalSpinner] = useState<boolean>(true);
  const [snackBarOpen, setSnackBarOpen] = useState(false);
  const tableData = ownerDocumentsDataBook?.[currentPage];

  //error handling for document download/view
  useEffect(() => {
    if (isDownloadingFailed || isFetchingOwnerDocumentURLFailed) {
      setDocDownloadErrorMessage('Apologies, the request to download/view the document failed. Please try again later or contact support. Thank you.');
      setSnackBarOpen(true);
    }
  }, [isDownloadingFailed, isFetchingOwnerDocumentURLFailed]);

  //error handling
  //show or hide spinner depending on what data has been loaded.
  useEffect(() => {
    if (isGettingCompanySuccess && isFetchingOwnerDetailsSuccess) {
      //life is good
      setShowLocalSpinner(false);
      setInitialLoadErrorMessage(null);
    } else if (isGettingCompanyFailed || isFetchingOwnerDetailsFailed) {
      //life is bad
      setShowLocalSpinner(false);
      setInitialLoadErrorMessage('There was an error loading the information. Try refreshing the page.');
    } else {
      setShowLocalSpinner(true);
    }
  }, [isGettingCompanySuccess, isGettingCompanyFailed, isFetchingOwnerDetailsSuccess, isFetchingOwnerDetailsFailed]);

  const handleAlertClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackBarOpen(false);
  };

  //only change the start index if we need to flag to the slice to fetch
  const setSearchPageIndex = (pageNumber: number) => {
    if (!ownerDocumentsDataBook[pageNumber]) {
      const newIndex = pageNumber * rangeSize;
      setRecordStartIndex(newIndex);
    }
  };

  const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPageNumber: number) => {
    setSearchPageIndex(newPageNumber);
    setCurrentPage(newPageNumber);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setCurrentPage(0);
    setRecordStartIndex(0);
    setRangeSize(parseInt(event.target.value));
  };

  //if you successfully get the url, open the doc in a new tab
  useEffect(() => {
    if (isFetchingOwnerDocumentURLSuccess && selectedDocumentUuid) {
      setDocDownloadErrorMessage(null);
      setSnackBarOpen(false);
      window.open(ownerDocumentURL);
    }
  }, [isFetchingOwnerDocumentURLSuccess]);

  return (
    <Box>
      {showLocalSpinner ? (
        <Stack sx={{ background: '#ffffff', borderRadius: '24px', height: 'calc( 100vh - 144px )', display: 'flex', justifyContent: 'center', alignItems: 'center', paddingBottom: 2, zIndex: 0 }}>
          <SpinnerWithNoConfirmation />
        </Stack>
      ) : !initialLoadErrorMessage ? (
        <Stack
          sx={{
            background: '#ffffff',
            borderRadius: '24px',
            height: 'calc( 100vh - 144px )',
            paddingBottom: 2,
            zIndex: 0,
          }}
        >
          {companyInfo.OwnerPortalEnabled ? (
            isFetchingOwnerDocumentsFailed ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', flexDirection: 'column', alignItems: 'center', height: '100%', width: '100%', p: 2 }}>
                <Typography
                  style={{
                    fontFamily: "'Work Sans', sans-serif",
                    fontStyle: 'normal',
                    fontWeight: 'normal',
                    fontSize: '18px',
                    lineHeight: '22px',
                    color: 'red',
                    marginTop: '28px',
                    marginBottom: '28px',
                    textAlign: 'center',
                  }}
                >
                  {'Apologies, the request to view documents failed. Please try again later or contact support. Thank you'}
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%', width: '100%' }}>
                <Stack overflow="auto" paddingBottom={1}>
                  <Stack p={{ xs: '16px', md: '24px' }} gap={{ xs: 3, md: 3 }}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography
                        sx={{
                          fontFamily: 'Work Sans',
                          fontSize: '22px',
                          fontWeight: '600',
                        }}
                      >
                        Documents
                      </Typography>
                      <Button
                        variant="text"
                        sx={{
                          fontfamily: 'Work Sans',
                          fontSize: '18px',
                          fontWeight: '400',
                          display: 'none',
                          gap: '12px',
                          lineHeight: '22px',
                          textTransform: 'capitalize',
                          color: '#14142B',
                        }}
                      >
                        Download All
                        <img width={13} height={14} src={DownloadIcon} alt="export" style={{ cursor: 'pointer' }} />
                      </Button>
                    </Stack>
                    <Stack>
                      <Filter rangeSize={rangeSize} startIndex={recordStartIndex} ownerUuid={currentUser?.currentProfile.uuid || ''} />
                    </Stack>
                  </Stack>
                  <Divider sx={{ borderColor: '#A8A8A8' }} />
                  <Stack p={{ xs: '12px 12px 0 12px', md: '24px 24px 0 24px' }} overflow="hidden">
                    <TableContainer style={{ flex: 1 }}>
                      <Table stickyHeader size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell id="subTitle">Document Description</TableCell>
                            <TableCell id="subTitle">Property</TableCell>
                            <TableCell id="subTitle">Type</TableCell>
                            <TableCell id="subTitle">Date</TableCell>
                            <TableCell id="subTitle" align="center">
                              View
                            </TableCell>
                            <TableCell id="subTitle" align="center">
                              Download
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody style={{ left: '28px' }}>
                          {tableData?.Rows?.map((ownerDocumentDto: OwnerDocumentDto) => (
                            <TableRow key={ownerDocumentDto.DocumentUuid}>
                              <TableCell id="content">{ownerDocumentDto.DocumentDescription}</TableCell>
                              <TableCell id="content">{ownerDocumentDto.Property}</TableCell>
                              <TableCell id="content">{getFriendlyWordsFromDocumentType(ownerDocumentDto.Type)}</TableCell>
                              <TableCell id="content">{convertSqlDateToUSCalendarDate(ownerDocumentDto.Date)}</TableCell>
                              <TableCell>
                                <Box display="flex" justifyContent="center" alignItems="center" height={34}>
                                  {isFetchingOwnerDocumentURL && selectedDocumentUuid === ownerDocumentDto.DocumentUuid ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 0.6 }}>
                                      <CircularProgress size={20} />
                                    </Box>
                                  ) : (
                                    <Box>
                                      {!MIME_TYPES_VIEWABLE_IN_BROWSER.includes(getMimeTypeFromFileName(ownerDocumentDto.Filename) ?? '') ? (
                                        <Tooltip title="This file is not viewable" placement="top">
                                          <BrowserNotSupportedIcon style={{ height: 20, width: 20 }} />
                                        </Tooltip>
                                      ) : (
                                        <Tooltip title="View Document" placement="top">
                                          <img
                                            width={13}
                                            height={14}
                                            src={ReviewOtpIcon}
                                            alt="export"
                                            style={{ cursor: 'pointer' }}
                                            onClick={() => {
                                              setSelectedDocumentUuid(ownerDocumentDto.DocumentUuid);
                                              dispatch(
                                                fetchOwnerDocumentURL({
                                                  documentUuid: ownerDocumentDto.DocumentUuid,
                                                  fileType: '',
                                                }),
                                              );
                                            }}
                                          />
                                        </Tooltip>
                                      )}
                                    </Box>
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Box display="flex" justifyContent="center" alignItems="center" height={34}>
                                  {isDownloading && selectedDocumentUuid === ownerDocumentDto.DocumentUuid ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 0.6 }}>
                                      <CircularProgress size={20} />
                                    </Box>
                                  ) : (
                                    <Tooltip title="Download Document" placement="top">
                                      <img
                                        width={13}
                                        height={14}
                                        src={DownloadIcon}
                                        alt="export"
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => {
                                          setSelectedDocumentUuid(ownerDocumentDto.DocumentUuid);
                                          dispatch(
                                            getDownloading({
                                              documentUuid: ownerDocumentDto.DocumentUuid,
                                              fileType: '',
                                            }),
                                          );
                                        }}
                                      />
                                    </Tooltip>
                                  )}
                                </Box>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Stack>
                </Stack>
                <Box>
                  <TablePagination
                    component="div"
                    rowsPerPageOptions={[10, 50, 100, 200]}
                    count={ownerDocuments?.TotalCount || 0}
                    page={currentPage}
                    onPageChange={handleChangePage}
                    rowsPerPage={rangeSize}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                  />
                </Box>
              </Box>
            )
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', flexDirection: 'column', alignItems: 'center', height: '100%', width: '100%', p: 2 }}>
              <img src={ComingSoonImg} alt="Coming soon" style={{ height: '300px', width: '300px' }} />
              <Typography
                style={{
                  fontFamily: "'Work Sans', sans-serif",
                  fontStyle: 'normal',
                  fontWeight: 'normal',
                  fontSize: '20px',
                  lineHeight: '22px',
                  color: '#000000',
                  marginTop: '28px',
                  marginBottom: '28px',
                  textAlign: 'center',
                }}
              >
                Your account is currently not configured to use this feature, please contact your property manager.
              </Typography>
            </Box>
          )}
        </Stack>
      ) : (
        <Stack
          sx={{
            background: '#ffffff',
            borderRadius: '24px',
            height: 'calc( 100vh - 144px )',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            paddingBottom: 2,
            zIndex: 0,
          }}
        >
          <img src={Error500} alt="500" style={{ height: '35vh' }} />
          <Typography
            sx={{
              fontFamily: 'Work Sans',
              fontStyle: 'normal',
              fontWeight: 400,
              fontSize: '14px',
              lineHeight: '23px',
              color: '#9E9E9E',
              textAlign: 'center',
              marginTop: '10px',
            }}
          >
            {initialLoadErrorMessage.length === 0 ? '' : initialLoadErrorMessage}
          </Typography>
        </Stack>
      )}
      <Snackbar anchorOrigin={{ horizontal: 'center', vertical: 'top' }} open={snackBarOpen} autoHideDuration={4000} onClose={handleAlertClose}>
        <Alert onClose={handleAlertClose} severity="error">
          {initialLoadErrorMessage || docDownloadErrorMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}
